import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Upload, FileText, Printer, Copy } from "lucide-react";
import { PageHero, DraftNotice } from "@/components/common/SectionHeading";
import {
    fetchApplicantTypes, fetchPrograms, fetchRequirements, createApplication, saveApplicationStep,
    submitApplication, uploadDocument, lookupApplication,
} from "@/lib/lnlApi";

const STEPS = ["Path", "Program", "Personal", "Academic", "Requirements", "Upload", "Review", "Confirm"];

const STORAGE_KEY = "lnl_current_app_ref"; // stores reference + email only

function Stepper({ index }) {
    return (
        <ol className="grid grid-cols-4 md:grid-cols-8 gap-1 text-[10.5px] md:text-[11px] font-heading font-semibold uppercase tracking-wider" data-testid="app-stepper">
            {STEPS.map((s, i) => (
                <li key={s} className={`flex items-center gap-1.5 px-2 py-2 rounded-md ${i === index ? "bg-lnl-green text-white" : i < index ? "bg-lnl-green-bg text-lnl-green-deep" : "bg-white text-lnl-gray border border-lnl-border"}`}>
                    <span className="tabular-nums">{String(i + 1).padStart(2, "0")}</span> {s}
                </li>
            ))}
        </ol>
    );
}

export default function Apply() {
    const [params] = useSearchParams();
    const initialType = params.get("type") || "";
    const [types, setTypes] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [reqs, setReqs] = useState(null);

    const [step, setStep] = useState(0);
    const [ref, setRef] = useState(null);
    const [email, setEmail] = useState("");
    const [applicantType, setApplicantType] = useState(initialType);
    const [program, setProgram] = useState({ slug: "", intake: "AY 2026-2027", semester: "First Semester" });
    const [personal, setPersonal] = useState({ full_name: "", preferred_name: "", dob: "", nationality: "Filipino", mobile: "", address: "", municipality: "", province: "Pangasinan", guardian_name: "", guardian_relationship: "", guardian_mobile: "", guardian_email: "" });
    const [academic, setAcademic] = useState({ current_school: "", school_address: "", current_grade: "", year_completed: "", lrn: "" });
    const [checklist, setChecklist] = useState({});
    const [uploads, setUploads] = useState([]);
    const [consents, setConsents] = useState({ consent_accurate: false, consent_privacy: false, consent_communication: false, guardian_confirmation: false });
    const [submitted, setSubmitted] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { fetchApplicantTypes().then(setTypes); fetchPrograms().then(setPrograms); }, []);
    useEffect(() => { if (applicantType) fetchRequirements(applicantType).then(setReqs).catch(() => setReqs(null)); }, [applicantType]);

    // Resume from local reference (reference + email only; never sensitive data)
    useEffect(() => {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        try {
            const { reference, email: e } = JSON.parse(raw);
            if (reference && e) lookupApplication(reference, e).then((doc) => {
                setRef(reference); setEmail(e);
                setApplicantType(doc.applicant_type);
                if (doc.steps?.program) setProgram({ ...program, ...doc.steps.program });
                if (doc.steps?.personal) setPersonal({ ...personal, ...doc.steps.personal });
                if (doc.steps?.academic) setAcademic({ ...academic, ...doc.steps.academic });
                if (doc.steps?.requirements) setChecklist(doc.steps.requirements);
                if (doc.documents) setUploads(doc.documents);
                setStep(3);
                toast.success(`Resumed application ${reference}`);
            }).catch(() => localStorage.removeItem(STORAGE_KEY));
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const relevantPrograms = useMemo(() => {
        if (!applicantType) return programs;
        if (applicantType === "college-freshman" || applicantType === "college-transferee" || applicantType === "als-pept") return programs.filter((p) => p.level === "college");
        return [];
    }, [applicantType, programs]);

    const isMinor = useMemo(() => {
        if (!personal.dob) return applicantType && ["kindergarten", "elementary", "junior-high"].includes(applicantType);
        const age = (Date.now() - new Date(personal.dob).getTime()) / (365.25 * 24 * 3600 * 1000);
        return age < 18;
    }, [personal.dob, applicantType]);

    async function next() {
        // Persist per-step
        if (step === 0) {
            if (!applicantType) return toast.error("Select an applicant path.");
            if (!email) return toast.error("Enter a contact email so you can resume this application.");
            if (!ref) {
                setBusy(true);
                try {
                    const r = await createApplication({ applicant_type: applicantType, contact_email: email });
                    setRef(r.reference);
                    localStorage.setItem(STORAGE_KEY, JSON.stringify({ reference: r.reference, email }));
                    toast.success(`Draft created: ${r.reference}`);
                } catch (e) { setBusy(false); return toast.error("Could not start application."); }
                finally { setBusy(false); }
            }
        }
        if (ref) {
            const stepMap = { 1: "program", 2: "personal", 3: "academic", 4: "requirements", 5: "uploads" };
            const dataMap = { 1: program, 2: personal, 3: academic, 4: checklist, 5: { count: uploads.length } };
            if (stepMap[step]) {
                try { await saveApplicationStep(ref, stepMap[step], dataMap[step], email); } catch { toast.error("Could not save this step."); return; }
            }
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    }
    function back() { setStep((s) => Math.max(0, s - 1)); }

    async function onFileUpload(e, label) {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const r = await uploadDocument(ref, email, label, file);
            setUploads((u) => [...u, r.document]);
            toast.success(`Uploaded ${label}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed. Only PDF, JPG or PNG up to 8MB.");
        } finally { setBusy(false); e.target.value = ""; }
    }

    async function doSubmit() {
        if (!(consents.consent_accurate && consents.consent_privacy && consents.consent_communication)) {
            return toast.error("Please accept all required consents.");
        }
        if (isMinor && !consents.guardian_confirmation) return toast.error("Parent / guardian confirmation is required for minors.");
        setBusy(true);
        try {
            const res = await submitApplication(ref, email, consents);
            setSubmitted(res);
            setStep(STEPS.length - 1);
            localStorage.removeItem(STORAGE_KEY);
            toast.success("Application submitted");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit."); }
        finally { setBusy(false); }
    }

    return (
        <div data-testid="apply-page">
            <PageHero
                eyebrow="Apply online"
                title="Start your LNL application"
                description="A short, save-and-continue application. You'll receive a unique reference number after each step."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Admissions", to: "/admissions" }, { label: "Apply Online" }]}
            />
            <section className="py-10 bg-white">
                <div className="lnl-container max-w-4xl">
                    <Stepper index={step} />
                    <div className="mt-6 lnl-card p-6">
                        {step === 0 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 1 — Applicant path</h2>
                                <p className="text-sm text-lnl-gray mt-1">Choose the path that matches you. Foreign applicants use the Foreign Student Inquiry.</p>
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {types.filter((t) => t.code !== "foreign").map((t) => (
                                        <button key={t.code} onClick={() => setApplicantType(t.code)} className={`text-left lnl-card p-4 ${applicantType === t.code ? "border-lnl-green ring-1 ring-lnl-green/30" : ""}`} data-testid={`apply-path-${t.code}`}>
                                            <div className="font-heading font-semibold text-lnl-charcoal">{t.label}</div>
                                            <div className="text-xs text-lnl-gray mt-1">{t.short}</div>
                                        </button>
                                    ))}
                                </div>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-lnl-gray font-heading font-semibold">Contact email (used to resume)</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid="apply-email" />
                                    </div>
                                </div>
                                <div className="mt-4 text-xs text-lnl-gray">Foreign applicants: <Link to="/admissions/foreign-inquiry" className="lnl-link">use the Foreign Student Inquiry</Link> instead.</div>
                            </div>
                        )}
                        {step === 1 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 2 — Program or education level</h2>
                                <p className="text-sm text-lnl-gray mt-1">Choose the program you're applying to. Basic Education applicants will simply be routed to the correct grade level.</p>
                                {relevantPrograms.length > 0 ? (
                                    <select value={program.slug} onChange={(e) => setProgram({ ...program, slug: e.target.value })} className="mt-4 w-full rounded-md border border-lnl-border px-3 py-2.5 bg-white" data-testid="apply-program">
                                        <option value="">Select a program…</option>
                                        {relevantPrograms.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                                    </select>
                                ) : (
                                    <div className="mt-4 text-sm text-lnl-gray">For {applicantType}, program selection is not required — proceed to the next step.</div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                    <div>
                                        <label className="text-xs text-lnl-gray font-heading font-semibold">Intended academic year</label>
                                        <input value={program.intake} onChange={(e) => setProgram({ ...program, intake: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid="apply-intake" />
                                    </div>
                                    <div>
                                        <label className="text-xs text-lnl-gray font-heading font-semibold">Intended semester</label>
                                        <input value={program.semester} onChange={(e) => setProgram({ ...program, semester: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid="apply-semester" />
                                    </div>
                                </div>
                            </div>
                        )}
                        {step === 2 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 3 — Personal details</h2>
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        ["full_name", "Complete legal name"], ["preferred_name", "Preferred name (optional)"],
                                        ["dob", "Date of birth (YYYY-MM-DD)"], ["nationality", "Nationality"],
                                        ["mobile", "Mobile number"], ["address", "Complete address"],
                                        ["municipality", "Municipality / City"], ["province", "Province"],
                                    ].map(([k, l]) => (
                                        <div key={k}>
                                            <label className="text-xs text-lnl-gray font-heading font-semibold">{l}</label>
                                            <input value={personal[k] || ""} onChange={(e) => setPersonal({ ...personal, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid={`apply-personal-${k}`} />
                                        </div>
                                    ))}
                                </div>
                                {isMinor && (
                                    <div className="mt-6">
                                        <div className="lnl-eyebrow">Parent / guardian</div>
                                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {[
                                                ["guardian_name", "Guardian name"], ["guardian_relationship", "Relationship"],
                                                ["guardian_mobile", "Guardian mobile"], ["guardian_email", "Guardian email"],
                                            ].map(([k, l]) => (
                                                <div key={k}>
                                                    <label className="text-xs text-lnl-gray font-heading font-semibold">{l}</label>
                                                    <input value={personal[k] || ""} onChange={(e) => setPersonal({ ...personal, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid={`apply-guardian-${k}`} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {step === 3 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 4 — Academic background</h2>
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        ["current_school", "Current / last school"], ["school_address", "School address"],
                                        ["current_grade", "Current / completed grade level"], ["year_completed", "Year completed"],
                                        ["lrn", "Learner Reference Number (LRN) — when required"],
                                    ].map(([k, l]) => (
                                        <div key={k}>
                                            <label className="text-xs text-lnl-gray font-heading font-semibold">{l}</label>
                                            <input value={academic[k] || ""} onChange={(e) => setAcademic({ ...academic, [k]: e.target.value })} className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2.5" data-testid={`apply-academic-${k}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {step === 4 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 5 — Requirements checklist</h2>
                                <div className="mt-2"><DraftNotice tone="warn">Requirements may be updated. Confirm with Admissions before submission.</DraftNotice></div>
                                <ul className="mt-4 space-y-2">
                                    {(reqs?.items || []).map((r, i) => (
                                        <li key={i} className="flex items-start gap-3 lnl-card p-3">
                                            <input type="checkbox" checked={!!checklist[r]} onChange={(e) => setChecklist({ ...checklist, [r]: e.target.checked })} data-testid={`checklist-${i}`} />
                                            <span className="text-sm text-lnl-charcoal">{r}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {step === 5 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 6 — Upload documents</h2>
                                <p className="text-sm text-lnl-gray mt-1">Upload the items you have ready. PDF, JPG or PNG — up to 8MB per file.</p>
                                <div className="mt-4 space-y-2">
                                    {(reqs?.items || []).map((r, i) => {
                                        const uploaded = uploads.find((u) => u.label === r);
                                        return (
                                            <div key={i} className="lnl-card p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                                                <div className="flex-1 text-sm">
                                                    <div className="text-lnl-charcoal">{r}</div>
                                                    {uploaded && <div className="text-xs text-lnl-green-deep mt-1 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {uploaded.original_filename}</div>}
                                                </div>
                                                <label className="lnl-btn-secondary cursor-pointer text-xs" data-testid={`upload-${i}`}>
                                                    <Upload className="h-4 w-4" /> {uploaded ? "Replace" : "Upload"}
                                                    <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => onFileUpload(e, r)} disabled={busy} />
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 text-xs text-lnl-gray">Uploaded files are stored securely and are not publicly accessible.</div>
                            </div>
                        )}
                        {step === 6 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">Step 7 — Review & consent</h2>
                                <div className="mt-4 lnl-card p-4 text-sm">
                                    <div><b>Reference:</b> {ref}</div>
                                    <div><b>Applicant path:</b> {applicantType}</div>
                                    <div><b>Program:</b> {program.slug || "—"} ({program.intake}, {program.semester})</div>
                                    <div><b>Name:</b> {personal.full_name}</div>
                                    <div><b>Mobile:</b> {personal.mobile}</div>
                                    <div><b>Address:</b> {personal.address}, {personal.municipality}, {personal.province}</div>
                                    <div><b>Current school:</b> {academic.current_school} ({academic.current_grade})</div>
                                    <div><b>Documents uploaded:</b> {uploads.length}</div>
                                </div>
                                <div className="mt-4 space-y-2 text-sm">
                                    <label className="flex items-start gap-2"><input type="checkbox" checked={consents.consent_accurate} onChange={(e) => setConsents({ ...consents, consent_accurate: e.target.checked })} data-testid="consent-accurate" /> I confirm the information provided is accurate to the best of my knowledge.</label>
                                    <label className="flex items-start gap-2"><input type="checkbox" checked={consents.consent_privacy} onChange={(e) => setConsents({ ...consents, consent_privacy: e.target.checked })} data-testid="consent-privacy" /> I consent to LNL's privacy handling of my application data.</label>
                                    <label className="flex items-start gap-2"><input type="checkbox" checked={consents.consent_communication} onChange={(e) => setConsents({ ...consents, consent_communication: e.target.checked })} data-testid="consent-communication" /> I consent to receive admissions-related communication.</label>
                                    {isMinor && <label className="flex items-start gap-2"><input type="checkbox" checked={consents.guardian_confirmation} onChange={(e) => setConsents({ ...consents, guardian_confirmation: e.target.checked })} data-testid="consent-guardian" /> Parent / guardian confirmation (required for minors).</label>}
                                </div>
                            </div>
                        )}
                        {step === 7 && (
                            <div className="text-center py-4" data-testid="apply-confirmation">
                                <CheckCircle2 className="h-14 w-14 text-lnl-green mx-auto" />
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal mt-4">Application {submitted?.status || "Submitted"}</h2>
                                <div className="mt-1 text-lnl-gray">Save this reference number. You'll need it to check your status.</div>
                                <div className="mt-4 inline-flex items-center gap-2 lnl-card px-4 py-2">
                                    <FileText className="h-4 w-4 text-lnl-green" />
                                    <span className="font-heading font-bold text-lg">{ref}</span>
                                    <button onClick={() => { navigator.clipboard.writeText(ref); toast.success("Copied"); }} className="text-lnl-gray hover:text-lnl-green"><Copy className="h-4 w-4" /></button>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                                    <button onClick={() => window.print()} className="lnl-btn-secondary"><Printer className="h-4 w-4" /> Printable summary</button>
                                    <Link to={`/applicant/${ref}?email=${encodeURIComponent(email)}`} className="lnl-btn-primary">Open applicant dashboard <ArrowRight className="h-4 w-4" /></Link>
                                </div>
                                <div className="mt-6 text-xs text-lnl-gray">Next: LNL Admissions will review your application. You'll be contacted via {email}.</div>
                            </div>
                        )}

                        {step < STEPS.length - 1 && (
                            <div className="mt-6 flex items-center justify-between no-print">
                                <button onClick={back} disabled={step === 0 || busy} className="lnl-btn-secondary" data-testid="apply-back"><ArrowLeft className="h-4 w-4" /> Back</button>
                                {step === 6 ? (
                                    <button onClick={doSubmit} disabled={busy} className="lnl-btn-primary" data-testid="apply-submit">Submit application <ArrowRight className="h-4 w-4" /></button>
                                ) : (
                                    <button onClick={next} disabled={busy} className="lnl-btn-primary" data-testid="apply-next">Save & continue <ArrowRight className="h-4 w-4" /></button>
                                )}
                            </div>
                        )}
                    </div>
                    {ref && step < STEPS.length - 1 && (
                        <div className="mt-4 text-xs text-lnl-gray flex items-center gap-2">
                            <span>Draft saved: <b>{ref}</b>. You can return anytime — this browser will resume automatically.</span>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
